/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useCallback, useState} from 'react';
import {useAgentsAPI} from './useAgentsAPI';
import {type APIParams, METHODS} from '../../../hooks/abstractions/useTrusstedAgent';
import {AGENTS_API_EVENT_NAMES} from '../../../types/agents-api';

export type Voice = {
  voiceId: string;
  voiceProvider: string;
  name: string;
  description: string;
  previewUrl: string;
  labels: Record<string, string>;
  category: string;
};

export const useGetVoices = () => {
  const agentsAPI = useAgentsAPI();
  const [voices, setVoices] = useState<Voice[]>([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getVoices = useCallback(async () => {
    setIsLoadingVoices(true);
    setError(null);

    try {
      const result = await agentsAPI({
        eventName: AGENTS_API_EVENT_NAMES.ListVoices,
        methodName: METHODS.GET,
      } as APIParams);

      setVoices(result?.voices as Voice[]);
    } catch (err) {
      setError('Error fetching voices');
      console.error(err);
    } finally {
      setIsLoadingVoices(false);
    }
  }, [agentsAPI]);

  return {
    voices,
    isLoadingVoices,
    error,
    getVoices,
  };
};
